import { useNavigate } from 'react-router-dom'
import { CommunityDTO } from '../../models/community'
import { IconButton } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import AvatarEditor from 'react-avatar-editor'
import { AppRoutes } from '../../helpers/routes'
import CustomIconButton from '../../components/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import { isCurrentUser } from '../../helpers/utils'
import CommunitiesService from '../../services/CommunitiesService'
import CustomModal from '../../containers/Modal'
import ConfirmationModal from '../../components/Modal/ConfirmationModal'
import { useState } from 'react'
import { UserDTO } from '../../models/users'
import RemoveModeratorIcon from '@mui/icons-material/RemoveModerator'
import AddModeratorIcon from '@mui/icons-material/AddModerator'
import StarIcon from '@mui/icons-material/Star'
import ShieldIcon from '@mui/icons-material/Shield'

interface Props {
    community: CommunityDTO
    handleClose: () => void
    isCommunityAdmin: boolean
    mutate: boolean
    setMutate: (arg: boolean) => void
}
const MembersModal = ({ community, handleClose, isCommunityAdmin, mutate, setMutate }: Props) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [selectedMember, setSelectedMember] = useState<string>()
    const [communityAux, setCommunityAux] = useState<CommunityDTO>(community)
    const [members, setMembers] = useState<UserDTO[]>(community.members)
    const navigate = useNavigate()

    const deleteMember = (id: string) => {
        new CommunitiesService().removeMember(id, community.id).then((response) => {
            setIsModalOpen(false)
            if (response.status !== 200) return
            if (members.length === 1) handleClose()
            setMembers((prev) => prev.filter((x) => x.id !== id))
            setMutate(!mutate)
        })
    }

    const addAdmin = (user: UserDTO) => {
        new CommunitiesService().addAdmin(user.id, community.id).then((response) => {
            if (response.status !== 200) return
            setCommunityAux((prev) => ({ ...prev, administrators: [...prev.administrators, user] }))
            setMutate(!mutate)
        })
    }

    const removeAdmin = (id: string) => {
        new CommunitiesService().removeAdmin(id, community.id).then((response) => {
            if (response.status !== 200) return
            setCommunityAux((prev) => ({
                ...prev,
                administrators: prev.administrators.filter((x) => x.id !== id),
            }))
            setMutate(!mutate)
        })
    }

    const isThisUserAdmin = (user: UserDTO) =>
        communityAux.administrators.some((x) => x.id === user.id)

    const isThisUserFounder = (user: UserDTO) => communityAux.founder!.id === user.id

    return (
        <div className="flex flex-col w-[60vw] md:w-[25vw] gap-1 md:gap-2">
            <div className="flex items-center">
                <label className="text-secondary font-semibold">
                    Miembros de "{community.name}"
                </label>
                <IconButton
                    onClick={handleClose}
                    className="ml-auto p-0 text-primary"
                >
                    <ClearIcon />
                </IconButton>
            </div>
            <div className="flex flex-col gap-1 max-h-48 md:max-h-64 overflow-y-auto">
                {members.map((user) => (
                    <div
                        key={user.email}
                        className="flex mt-1 md:mt-2 justify-between bg-light rounded-[20px] p-2 shadow box-border w-full"
                    >
                        <div className="flex gap-2 md:gap-4 items-center">
                            <div
                                onClick={() => navigate(AppRoutes.user.replace(':userId', user.id))}
                                className="cursor-pointer"
                            >
                                <AvatarEditor
                                    image={!!user.picture ? user.picture : '/adminPhoto.png'}
                                    height={40}
                                    width={40}
                                    border={0}
                                    className="z-1 rounded-full pointer-events-none"
                                />
                            </div>
                            <span
                                onClick={() => navigate(AppRoutes.user.replace(':userId', user.id))}
                                className="cursor-pointer hover:underline"
                            >
                                {user.firstName + ' ' + user.lastName}
                            </span>
                        </div>
                        <div className="flex">
                            {isCommunityAdmin &&
                                !isCurrentUser(user) &&
                                !isThisUserFounder(user) &&
                                !isThisUserAdmin(user) && (
                                    <CustomIconButton
                                        icon={<DeleteIcon />}
                                        title="Eliminar miembro"
                                        onClick={() => {
                                            setSelectedMember(user.id)
                                            setIsModalOpen(true)
                                        }}
                                        className="px-[5px] md:px-[10px]"
                                    />
                                )}
                            {isThisUserFounder(user) && (
                                <CustomIconButton
                                    icon={<StarIcon />}
                                    title="Fundador"
                                    className="px-[5px] md:px-[10px]"
                                />
                            )}
                            {isCurrentUser(community.founder!) &&
                                isThisUserAdmin(user) &&
                                !isThisUserFounder(user) && (
                                    <CustomIconButton
                                        icon={<RemoveModeratorIcon />}
                                        title="Quitar administrador"
                                        className="px-[5px] md:px-[10px]"
                                        onClick={() => removeAdmin(user.id)}
                                    />
                                )}
                            {isCurrentUser(community.founder!) &&
                                !isThisUserAdmin(user) &&
                                !isThisUserFounder(user) && (
                                    <CustomIconButton
                                        icon={<AddModeratorIcon />}
                                        title="Hacer administrador"
                                        className="px-[5px] md:px-[10px]"
                                        onClick={() => addAdmin(user)}
                                    />
                                )}
                            {!isCurrentUser(community.founder!) &&
                                isThisUserAdmin(user) &&
                                !isThisUserFounder(user) && (
                                    <CustomIconButton
                                        icon={<ShieldIcon />}
                                        title="Administrador"
                                        className="px-[5px] md:px-[10px]"
                                    />
                                )}
                        </div>
                    </div>
                ))}
            </div>
            <CustomModal
                isOpen={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
            >
                <ConfirmationModal
                    title="¿Estás seguro de que querés eliminar a este miembro?"
                    subtitle="Esta acción es irreversible"
                    confirm={() => (selectedMember ? deleteMember(selectedMember) : {})}
                    close={() => setIsModalOpen(false)}
                />
            </CustomModal>
        </div>
    )
}

export default MembersModal
